import { useCallback, useSyncExternalStore } from 'react';

/**
 * Three choices, not a boolean.
 *
 * A two-state switch can set a theme but can never give the OS back, and someone who
 * switches their machine at sunset expects the page to follow again. `system` is the
 * default and stays the default until a deliberate choice is made.
 */
export type Theme = 'system' | 'light' | 'dark';

const KEY = 'zsoogi:theme';
const listeners = new Set<() => void>();

function isTheme(value: unknown): value is Theme {
  return value === 'system' || value === 'light' || value === 'dark';
}

/** Read the stored choice. Storage can throw in private mode, so a failure means `system`. */
export function storedTheme(): Theme {
  try {
    const raw = localStorage.getItem(KEY);
    return isTheme(raw) ? raw : 'system';
  } catch {
    return 'system';
  }
}

/**
 * `system` removes the attribute entirely rather than setting it, which is what hands the
 * decision back to the `prefers-color-scheme` block in index.css.
 */
export function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  if (theme === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', theme);
}

export function setTheme(theme: Theme): void {
  applyTheme(theme);
  try {
    if (theme === 'system') localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, theme);
  } catch {
    // A browser that refuses storage still gets the theme for this tab; it just will not
    // remember. Worth nothing more than losing the choice on reload.
  }
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  // Another tab changing the choice should move this one too.
  const onStorage = (e: StorageEvent) => {
    if (e.key !== KEY) return;
    applyTheme(storedTheme());
    listener();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
}

/** The current choice, and a setter. Not what is *rendered* — `system` resolves at paint. */
export function useTheme(): [Theme, (next: Theme) => void] {
  const theme = useSyncExternalStore(subscribe, storedTheme, () => 'system' as Theme);
  return [theme, useCallback(setTheme, [])];
}
